import React, { useEffect, useState, useCallback } from 'react';
import SalesActionLog from '../../components/salesAction/SalesActionLog';
import { useSnackbar } from 'notistack'; 
import { getActionList, saveSalesAction, deleteSalesAction } from '../../lib/api/salesAction'; 
import { getActivityList } from '../../lib/api/salesActivity';
import { Box } from '@mui/material';

const SalesActionContainer = ({ toggleCalFull, isCalFull }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [isSaving, setIsSaving] = useState(false);
    const [events, setEvents] = useState([]);
    const [actionEvents, setActionEvents] = useState([]);

    // 🚀 [구조 격상] 하위 SalesActionLog에서 올려 보낸 선택된 날짜 상태를 최상위에서 관리합니다.
    // 기본값은 기존과 동일하게 2026년 9pt 10일로 초기화합니다.
    const [selectedDate, setSelectedDate] = useState(new Date());

    // 🚀 [Helper 함수] Date 객체를 "YYYY-MM-DD" 포맷 문자열로 변환하는 함수
    const formatDateToYMD = (dateObj) => {
        if (!dateObj || !(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
            return '';
        }
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const [shouldResetForm, setShouldResetForm] = useState(false);

    const handleDateChange = (nextDate, action) => {
        setSelectedDate(nextDate);

        if (action === 'reset') {
            setShouldResetForm(true); 
        }
    };

    const fetchInitialData = useCallback(async () => {
        try {
            const localUser = localStorage.getItem('user');
            let usrUuid = '';

            if (localUser) {
                const parsedUser = JSON.parse(localUser);
                usrUuid = parsedUser.usrUuid || parsedUser.userUuid || '';
            }

            const currentPath = window.location.pathname;
            if (currentPath !== '/salesAction') {
                usrUuid = '';
            }

            // 🚀 현재 선택되어 있는 오늘 날짜를 "YYYY-MM-DD" 포맷 문자열로 변환합니다.
            const targetDate = formatDateToYMD(selectedDate);

            // 🚀 API 호출 시 usrUuid와 함께 오늘 날짜(targetDate)를 파라미터로 넘겨줍니다.
            const [activityRes, actionRes] = await Promise.all([
                getActivityList(usrUuid, targetDate),
                getActionList(usrUuid, targetDate)
            ]);

            // 1️⃣ 기존 Activity 리스트 맵핑 및 세팅
            if (activityRes.data && activityRes.data.list && activityRes.data.list.length > 0) {
                const activityEvents = activityRes.data.list.map(item => ({
                    id: '', 
                    title: item.title,
                    start: item.startDatetime, 
                    end: item.endDatetime,
                    backgroundColor: '#1976d2', 
                    borderColor: '#1976d2',
                    allDay: false, 
                    extendedProps: {
                        companyUuid: item.companyUuid,
                        content: item.activityContent,
                        usrUuid: item.usrUuid,
                        usrNm: item.usrNm,
                        meetPlace: item.meetPlace,
                        companyNm: item.companyNm,
                        actionType:item.actionType,
                        type: 'activity',
                    }
                }));
                setEvents(activityEvents);
            } else {
                setEvents([]);
            }

            // 2️⃣ 새로운 Action 리스트 맵핑 및 세팅
            if (actionRes.data && actionRes.data.list && actionRes.data.list.length > 0) {
                console.log(actionRes.data);
                const mappedActions = actionRes.data.list.map(item => ({
                    id: item.actionId, 
                    title: item.title,
                    start: item.startDatetime, 
                    end: item.endDatetime,
                    backgroundColor: '#2e7d32', 
                    borderColor: '#2e7d32',
                    allDay: false, 
                    extendedProps: {
                        companyUuid: item.companyUuid,
                        content: item.actionContent, 
                        usrUuid: item.usrUuid,
                        usrNm: item.usrNm,
                        meetPlace: item.meetPlace,
                        companyNm: item.companyNm,
                        actionType:item.actionType,
                        cstNm: item.cstNm,
                        prjUuid: item.prjUuid,
                        prjTyp: item.prjTyp,
                        cmpGrade: item.cmpGrade,
                        cmpStatus: item.cmpStatus,
                        cstGrade: item.cstGrade,
                        type: 'action',
                        customProjectNm: item.customProjectNm,
                    }
                }));
                setActionEvents(mappedActions);
            } else {
                setActionEvents([]);
            }

        } catch (err) {
            console.error('영업활동 및 액션 목록 로드 오류:', err);
            const message = err.response?.data?.message || err.message || '목록을 불러오는 중 오류가 발생했습니다.';
            enqueueSnackbar(message, { variant: 'error' });
        }
    }, [enqueueSnackbar, selectedDate]); // 🚀 selectedDate가 변경될 때마다 데이터를 다시 불러오도록 의존성 배열에 추가합니다.

    // 🚀 날짜가 변경되었을 때도 리스트가 새로고침되도록 useEffect 의존성도 맞춰집니다.
    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);


    // 🚀 [수정 사항 반영] 폼 데이터의 시간과 컨테이너의 selectedDate 날짜를 조합하여 API 전송 데이터 바인딩
    const onSave = async (formData, onSuccess) => {
        setIsSaving(true);
        try {
            // 하위 타임라인에서 선택되어 격상 보관 중인 날짜를 "YYYY-MM-DD" 형태로 파싱
            const targetDateStr = formatDateToYMD(selectedDate);

            if (!targetDateStr) {
                throw new Error('유효한 날짜가 선택되지 않았습니다.');
            }

            // ⏱️ [시간 유효성 검사 추가]
            const startTime = formData.startTime; // 예: "05:30" 또는 "08:00"
            const endTime = formData.endTime;     // 예: "23:30" 또는 "18:00"

            if (!startTime || !endTime) {
                throw new Error('시작 시간과 종료 시간을 모두 입력해주세요.');
            }

            // 1. 시작 시간 최소 제한 (06:00)
            if (startTime < '06:00') {
                throw new Error('시작 시간은 06:00 이후로만 설정할 수 있습니다.');
            }

            // 2. 종료 시간 최대 제한 (23:00)
            if (endTime > '23:00') {
                throw new Error('종료 시간은 23:00 까지만 설정할 수 있습니다.');
            }

            // 3. 시작 시간이 종료 시간보다 늦거나 같은지 체크
            if (startTime >= endTime) {
                throw new Error('시작 시간은 종료 시간보다 빨라야 합니다.');
            }


            // 💡 [요구사항 가공] 시간은 자식 폼데이터 속성을 그대로 사용하고, 날짜는 가져온 컨테이너 상태를 조합
            const startDatetime = `${targetDateStr} ${startTime}:00`;
            const endDatetime = `${targetDateStr} ${endTime}:00`;

            const finalData = {
                actionId: formData.actionId || null,
                title: formData.title,
                companyUuid: formData.companyUuid,
                meetPlace : formData.meetPlace,
                actionContent: formData.content,                
                startDatetime: startDatetime,
                endDatetime: endDatetime,
                usrUuid: formData.usrUuid || '',
                dptUuid: formData.dptUuid || '',
                prjUuid: formData.prjUuid || '',
                actionType: formData.actionType,
                customProjectNm: formData.customProjectNm || '',
            };
            console.log(finalData);
            const response = await saveSalesAction(finalData);
            if (response.data.result === 'success') {
                enqueueSnackbar('성공적으로 저장되었습니다.', { variant: 'success' });

                if (typeof onSuccess === 'function') {
                    onSuccess(); // 🚀 여기서 자식의 handleResetForm()이 실행됨!
                }

                fetchInitialData();
            }
        } catch (err) {
            console.error('저장 오류:', err);
            // 에러 메시지가 사용자 정의 Error 객체에서 던져진 경우와 서버 에러인 경우를 모두 처리
            const message = err.response?.data?.message || err.message || '저장 중 오류가 발생했습니다.';
            enqueueSnackbar(message, { variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };


    // 부모 컴포넌트
    const onDelete = async (actionId, onSuccess) => { // 🚀 onSuccess 콜백 추가
        if (!window.confirm('정말 삭제하시겠습니까?')) return;
        setIsSaving(true);
        try {
            const response = await deleteSalesAction({ actionId });
            if (response.data.result === 'success') {
                enqueueSnackbar('성공적으로 삭제되었습니다.', { variant: 'success' });
                
                // 🚀 자식이 넘겨준 리셋 함수가 있다면 실행!
                if (typeof onSuccess === 'function') {
                    onSuccess();
                }

                fetchInitialData(); 
            }
        } catch (err) {
            console.error('삭제 오류:', err);
            const message = err.response?.data?.message || err.message || '삭제 중 오류가 발생했습니다.';
            enqueueSnackbar(message, { variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };



    const handleEventClick = (clickInfo) => {
        console.log('클릭한 이벤트 정보:', clickInfo.event);
    };

    const btnStyle = { 
        display: 'flex',          
        alignItems: 'center',     
        justifyContent: 'center',  
        padding: '8px',           
        cursor: 'pointer', 
        backgroundColor: '#fff', 
        border: '1px solid #ccc', 
        borderRadius: '4px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        transition: 'all 0.2s',
        '&:hover': {              
            backgroundColor: '#f5f5f5',
            borderColor: '#999'
        }
    };

    return (
        <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
          {window.location.pathname !== '/salesAction' && (
              <Box 
                  sx={{ 
                      position: 'absolute', 
                      top: '6px', 
                      right: '16px', 
                      zIndex: 100 
                  }}
              >
                  <button 
                      onClick={toggleCalFull} 
                      style={btnStyle}
                      title={isCalFull ? "원래 크기로 축소" : "화면 가득 확장"}
                  >
                      {isCalFull ? (
                          <svg xmlns="http://w3.org" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                              <path d="M4 14h6v6M20 10h-6V4M14 10l7-7M10 14l-7 7"/>
                          </svg>
                      ) : (
                          <svg xmlns="http://w3.org" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
                          </svg>
                      )}
                  </button>
              </Box>
          )}
            {/* 🚀 [연계 반영] 하위 SalesActionLog 프롭스에 컨테이너 제어 상태 및 상태변경 위임 함수 바인딩 */}
            <SalesActionLog 
                events={events}
                actionEvents={actionEvents}
                handelEventClick={handleEventClick}
                onSaveAction={onSave}
                onDeleteAction={onDelete}
                isLocationValid={isCalFull || window.location.pathname === '/salesAction'}
                isCalFull={isCalFull}
                selectedDate={selectedDate}
                onDateChange={handleDateChange}
                // 👇 리셋 신호 상태와 리모컨(세터 함수)을 추가로 내려줍니다.
                shouldResetForm={shouldResetForm}
                setShouldResetForm={setShouldResetForm}
            />
        </Box>
    );
};

export default SalesActionContainer;
